// quizStateManager.js
// Global session and blend cache manager for Candlecraft quiz and blend logic


(function() {
  if (window.quizStateManager) return; // Prevent double-init

  const BLEND_CACHE_KEY = 'blendSession.';
  const LAST_BLEND_ID_KEY = 'lastBlendId';
  const DEFAULT_API_BASE = 'https://api.candlecraftcollective.com';
  const API_BASE = String(window.CANDLECRAFT_API_BASE || DEFAULT_API_BASE).replace(/\/+$/, '');
  window.CANDLECRAFT_API_BASE = API_BASE;

  // Customer portal (myscents). Used to record anonymous quiz runs so they
  // can be claimed when the customer later logs into the portal (P0.1 of the Scent DNA roadmap).
  const DEFAULT_PORTAL_BASE = 'https://myscents.candlecraftcollective.com';
  const PORTAL_BASE = String(window.CANDLECRAFT_PORTAL_BASE || DEFAULT_PORTAL_BASE).replace(/\/+$/, '');
  window.CANDLECRAFT_PORTAL_BASE = PORTAL_BASE;

  // The browser-identity token is shared with the portal via a cookie on the root domain.
  // It must be readable by the portal subdomain, so it is set with domain=.<root>.
  // Configurable so non-prod environments (where the storefront and portal live on
  // different root domains) can set an empty value ⇒ host-only cookie, no domain attr.
  const BROWSER_TOKEN_KEY = 'ccc_browser_token';
  const DEFAULT_COOKIE_DOMAIN = '.candlecraftcollective.com';
  const COOKIE_DOMAIN = (window.CANDLECRAFT_COOKIE_DOMAIN !== undefined)
    ? String(window.CANDLECRAFT_COOKIE_DOMAIN)
    : DEFAULT_COOKIE_DOMAIN;


  window.quizStateManager = {
    // Key prefix for this manager's sessionStorage entries. It was never defined before, so keys
    // were written as "undefined<key>"; clearAll removes those too.
    prefix: 'ccQuiz.',

    // In-memory cache for current session
    _blendCache: {},
  

    // Set a value in sessionStorage with prefix
    set: function(key, value) {
      if (!key) return;
      try {
        sessionStorage.setItem(this.prefix + key, JSON.stringify(value));
      } catch (e) {
        console.warn("SessionStorage error:", e, key);
      }
    },

    // Get a value from sessionStorage with prefix
    get: function(key) {
      if (!key) return null;
      try {
        const raw = sessionStorage.getItem(this.prefix + key);
        return raw ? JSON.parse(raw) : null;
      } catch (e) {
        console.warn("SessionStorage get error:", e, key);
        return null;
      }
    },

    // Remove a value from sessionStorage with prefix
    remove: function(key) {
      if (!key) return;
      sessionStorage.removeItem(this.prefix + key);
    },

    // Clear all quiz session keys
    clearAll: function() {
      Object.keys(sessionStorage)
        .filter(k => k.startsWith(this.prefix) || k.startsWith('undefined') || k === 'quizAnswers')
        .forEach(k => sessionStorage.removeItem(k));
    },

    getBlendCache: function(blendId) {
      if (!blendId) return null;
      if (this._blendCache[blendId]) return this._blendCache[blendId];
      try {
        const raw = sessionStorage.getItem(BLEND_CACHE_KEY + blendId);
        if (raw) {
          const data = JSON.parse(raw);
          this._blendCache[blendId] = data;
          return data;
        }
      } catch (e) {}
      return null;
    },

    setBlendCache: function(blendId, data) {
      if (!blendId || !data) return;
      this._blendCache[blendId] = data;
      try {
        sessionStorage.setItem(BLEND_CACHE_KEY + blendId, JSON.stringify(data));
      } catch (e) {}
    },

    setLastBlendId: function(blendId) {
      if (!blendId) return;
      try {
        localStorage.setItem(LAST_BLEND_ID_KEY, blendId);
      } catch (e) {}
    },
    getLastBlendId: function() {
      try {
        return localStorage.getItem(LAST_BLEND_ID_KEY);
      } catch (e) { return null; }
    },
    clearLastBlendId: function() {
      try {
        localStorage.removeItem(LAST_BLEND_ID_KEY);
      } catch (e) {}
    },
    // Azure blend fetch
    getBlendUrl: function(blendId) {
      return `${API_BASE}/blend/${encodeURIComponent(blendId)}`;
    },
    async fetchBlendFromAzure(blendId) {
      if (!blendId) return null;
      const url = this.getBlendUrl(blendId);
      try {
        const res = await fetch(url);
        if (!res.ok) throw new Error("Blend not found in Azure");
        const data = await res.json();
        this.setBlendCache(blendId, data);
        return data;
      } catch (err) {
        if (window.logErrorToAzure) window.logErrorToAzure({
          message: "Failed to fetch blend from Azure",
          stack: err.stack,
          functionName: "quizStateManager",
          payload: { blendId }
        });
        return null;
      }
    },
    async restoreBlendFromAzure(blendId) {
      let blend = this.getBlendCache(blendId);
      if (blend) return blend;
      return await this.fetchBlendFromAzure(blendId);
    },
    // Quiz v2: question and answer text only; scoring stays on the server.
    async fetchQuizV2() {
      const res = await fetch(`${API_BASE}/quiz/v2`);
      if (!res.ok) throw new Error('Quiz API error: ' + res.status);
      return await res.json();
    },
    // Quiz v2: the engine scores the answers, builds the formula, writes the copy and saves the blob.
    // answers: one 0-based answer index per question. The response's blendId is bare (no "blend-").
    // The engine can take ~40s at worst (two 20s copy attempts), so give up after 60s instead of
    // leaving the loader up forever. A timeout throws with status 0 and code "timeout".
    async createBlendV2(answers, { leaveOut = [], timeoutMs = 60000 } = {}) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), timeoutMs);
      let res;
      try {
        res = await fetch(`${API_BASE}/blend`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ quizVersion: 'v2', answers, leaveOut, browserToken: this.getOrCreateBrowserToken() }),
          signal: controller.signal
        });
      } catch (fetchErr) {
        if (fetchErr.name !== 'AbortError') throw fetchErr;
        const err = new Error('Blend API timeout after ' + timeoutMs + 'ms');
        err.status = 0;
        err.code = 'timeout';
        throw err;
      } finally {
        clearTimeout(timer);
      }
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const err = new Error('Blend API error: ' + res.status + (data.error ? ' ' + data.error : ''));
        err.status = res.status;
        err.code = data.error;
        if (window.logErrorToAzure) window.logErrorToAzure({
          message: "Blend v2 API error",
          stack: err.stack,
          functionName: "quizStateManager",
          payload: { status: res.status, error: data.error }
        });
        throw err;
      }
      return data;
    },

    // Builds the canonical share URL for a given blendId or the last-known one.
    buildBlendLink(blendId) {
      const id = blendId || this.getLastBlendId();
      return id ? `/products/custom-blend?id=${encodeURIComponent(id)}` : '/products/custom-blend';
    },

    // Ensures any <a> pointing at /products/custom-blend has an ?id=…
    // Useful for cart drawers, order confirmation, etc.
    ensureBlendLinksHaveId(root) {
      try {
        const id = this.getLastBlendId();
        if (!id) return;
        const scope = root || document;
        scope.querySelectorAll('a[href*="/products/custom-blend"]').forEach(a => {
          const href = a.getAttribute('href') || '';
          if (href.includes('?id=')) return; // already good
          try {
            const u = new URL(href, window.location.origin);
            if (!u.searchParams.get('id')) {
              u.searchParams.set('id', id);
            }
            // Write back a relative URL for cleanliness
            a.setAttribute('href', u.pathname + (u.search ? u.search : ''));
          } catch {
            // Fallback for odd relative URLs
            a.setAttribute('href', `/products/custom-blend?id=${encodeURIComponent(id)}`);
          }
        });
      } catch (_) {}
    },

    // ── Portal anonymous-blend linking (P0.1) ───────────────────────────────
    // Returns the per-browser token, creating it once and reusing it across all
    // quiz runs. Persisted in localStorage and mirrored to a shared-domain cookie
    // so the portal can read it at login and claim every session for this browser.
    getOrCreateBrowserToken: function() {
      let token = null;
      try {
        token = localStorage.getItem(BROWSER_TOKEN_KEY);
      } catch (e) {}
      if (!token) {
        try {
          token = (window.crypto && crypto.randomUUID)
            ? crypto.randomUUID()
            : 'b-' + Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
          localStorage.setItem(BROWSER_TOKEN_KEY, token);
        } catch (e) {}
      }
      if (!token) return null;
      try {
        // Mirror to a cookie readable by the portal subdomain (shared root domain).
        // Omit the domain attribute entirely when COOKIE_DOMAIN is blank so non-prod
        // environments set a valid host-only cookie instead of a rejected one.
        const domainPart = COOKIE_DOMAIN ? ` domain=${COOKIE_DOMAIN};` : '';
        document.cookie =
          `${BROWSER_TOKEN_KEY}=${token};${domainPart} path=/; SameSite=Lax; Secure; max-age=2592000`;
      } catch (e) {}
      return token;
    },

    // Parses a "Label:60,Label:40" breakdown string into a numeric { Label: 60, … }
    // object. Accepts an already-parsed object and passes it through unchanged.
    parseCategories: function(breakdown) {
      if (breakdown && typeof breakdown === 'object') return breakdown;
      const out = {};
      if (typeof breakdown !== 'string') return out;
      breakdown.split(',').forEach(pair => {
        const idx = pair.lastIndexOf(':');
        if (idx === -1) return;
        const label = pair.slice(0, idx).trim();
        const value = Number(pair.slice(idx + 1));
        if (label && Number.isFinite(value)) out[label] = value;
      });
      return out;
    },

    // Records an anonymous quiz run with the portal. Fire-and-forget — never blocks
    // or breaks the quiz UX. The portal claims these rows when the customer logs in.
    recordQuizSession: function({ blendId, categories, quizVersion } = {}) {
      try {
        const browserToken = this.getOrCreateBrowserToken();
        if (!browserToken) return;

        // Portal expects the BARE Azure id — it prepends "blend-" itself.
        const bareBlendId = blendId ? String(blendId).replace(/^blend-/, '') : null;
        const parsedCategories = this.parseCategories(categories);
        if (!Object.keys(parsedCategories).length) return; // nothing useful to record

        const body = {
          browser_token: browserToken,
          blend_id: bareBlendId,
          categories: parsedCategories,
        };
        if (quizVersion) body.quiz_version = quizVersion;

        fetch(`${PORTAL_BASE}/api/quiz-sessions`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        }).catch(() => {}); // silent — recording is best-effort
      } catch (e) {
        // Never let portal recording interfere with the quiz.
      }
    },

  };

  // Backward-compat for older template calls that still reference window.getBlendUrl(...)
  if (typeof window.getBlendUrl !== 'function') {
    window.getBlendUrl = function(blendId) {
      return window.quizStateManager.getBlendUrl(blendId);
    };
  }

  window.injectBlendCardEmbed = function injectBlendCardEmbed(blendId) {
    if (!blendId) return;
    const target = document.querySelector('[data-blend-card-target]');
    if (!target) return;

    // Strip "blend-" prefix — portal embed route expects the bare Azure ID
    const bareId = String(blendId).replace(/^blend-/, '');
    const embedUrl = `${PORTAL_BASE}/embed/${bareId}`;
    window.ccDebug?.('blend card embed', embedUrl);

    target.innerHTML = `
      <iframe
        src="${embedUrl}"
        id="cc-blend-card-frame"
        scrolling="no"
        frameborder="0"
        style="width:100%;border:none;min-height:520px;transition:height 0.2s ease;"
        title="Your blend"
      ></iframe>
    `;

    window.addEventListener('message', function onCardHeight(e) {
      if (e.origin !== PORTAL_BASE) return;
      if (e.data?.type !== 'cc-card-height' && e.data?.type !== 'embed-height') return;
      const frame = document.getElementById('cc-blend-card-frame');
      if (frame) frame.style.height = e.data.height + 'px';
    });
  };
})();
